// 2.D.5 — Bulk-actions toolbar.
//
// Nakyy kun selectedIds.size > 0. Tarjoaa actionit valittujen asseteille:
//   - Poista (vahvistus-modaalin kanssa)
//   - Aseta rating (0-5)
//   - Aseta color label
//   - Aseta flag (pick/reject/unflagged)
//
// "Move to project" ja edistyneemmat actionit voi tulla 2.D.7+:ssa kun
// project-detail-naekyma valmis.

export type SelectionAction =
  | { type: "delete" }
  | { type: "set-rating"; rating: number }
  | { type: "set-color-label"; colorLabel: "none" | "red" | "yellow" | "green" | "blue" | "purple" }
  | { type: "set-flag"; flag: "unflagged" | "pick" | "reject" };

export type SelectionToolbarProps = {
  count: number;
  onAction: (action: SelectionAction) => void;
  onClear: () => void;
  isProcessing?: boolean;
};

export function SelectionToolbar({
  count,
  onAction,
  onClear,
  isProcessing = false,
}: SelectionToolbarProps) {
  if (count === 0) return null;
  return (
    <div className="sticky top-0 z-10 mb-4 flex flex-wrap items-center gap-3 rounded-md border border-slate-700 bg-slate-900/95 px-4 py-2 backdrop-blur">
      <span className="text-sm font-medium text-slate-100">{count} valittu</span>

      <div className="ml-2 flex items-center gap-1">
        {[0, 1, 2, 3, 4, 5].map((r) => (
          <button
            key={r}
            type="button"
            disabled={isProcessing}
            onClick={() => onAction({ type: "set-rating", rating: r })}
            className="rounded px-2 py-1 text-sm text-amber-300 hover:bg-slate-800 disabled:opacity-50"
            title={`Aseta ${r} tahtea`}
          >
            {r === 0 ? "0★" : "★".repeat(r)}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-1">
        <FlagBtn label="Pick" flag="pick" onAction={onAction} disabled={isProcessing} />
        <FlagBtn label="Reject" flag="reject" onAction={onAction} disabled={isProcessing} />
        <FlagBtn
          label="Tyhjenna"
          flag="unflagged"
          onAction={onAction}
          disabled={isProcessing}
        />
      </div>

      <div className="flex items-center gap-1">
        {(["red", "yellow", "green", "blue", "purple", "none"] as const).map((c) => (
          <ColorBtn key={c} color={c} onAction={onAction} disabled={isProcessing} />
        ))}
      </div>

      <div className="ml-auto flex items-center gap-2">
        <button
          type="button"
          disabled={isProcessing}
          onClick={() => onAction({ type: "delete" })}
          className="rounded-md border border-red-700 bg-red-950/40 px-3 py-1 text-sm text-red-200 hover:bg-red-950 disabled:opacity-50"
        >
          Poista
        </button>
        <button
          type="button"
          onClick={onClear}
          className="rounded-md px-3 py-1 text-sm text-slate-400 hover:bg-slate-800 hover:text-slate-100"
        >
          Tyhjenna valinta
        </button>
      </div>
    </div>
  );
}

function FlagBtn({
  flag,
  label,
  onAction,
  disabled,
}: {
  flag: "pick" | "reject" | "unflagged";
  label: string;
  onAction: (a: SelectionAction) => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onAction({ type: "set-flag", flag })}
      className="rounded px-2 py-1 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-50"
    >
      {label}
    </button>
  );
}

const COLOR_HEX: Record<string, string> = {
  red: "bg-red-500",
  yellow: "bg-yellow-500",
  green: "bg-green-500",
  blue: "bg-blue-500",
  purple: "bg-purple-500",
  none: "border border-slate-500 bg-transparent",
};

function ColorBtn({
  color,
  onAction,
  disabled,
}: {
  color: "red" | "yellow" | "green" | "blue" | "purple" | "none";
  onAction: (a: SelectionAction) => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onAction({ type: "set-color-label", colorLabel: color })}
      title={`Color label: ${color}`}
      className={`h-5 w-5 rounded-full ${COLOR_HEX[color] ?? ""} hover:scale-110 disabled:opacity-50`}
    />
  );
}
